/**
 * Correlation Context
 *
 * Per-launch CorrelationVector container. A "launch" is one mount of the
 * AtomPlayer / useEngagement hook — every backend request issued during that
 * launch derives a child CV from a single root, so App Insights can correlate
 * them as one logical operation.
 *
 * Exposes a singleton because there is only ever one active player mount in
 * V1. If multi-player support arrives, this becomes per-context state.
 *
 * @module http/correlation-context
 */
import { CorrelationVector } from 'mscv';

let activeLaunchCv: CorrelationVector | null = null;
let launchCorrelationId: string | null = null;

/**
 * Starts a new launch context. Replaces any existing context.
 * Generates both a CorrelationVector (for MS-CV distributed tracing) and a
 * launch-scoped correlationId (GUID) that stays the same for all calls in
 * this launch.
 * @returns The base CV value for the new launch (e.g., `tul4NUsfs9Cl7mOf.0`).
 */
export function startLaunchContext(): string {
    activeLaunchCv = CorrelationVector.createCorrelationVector(1);
    launchCorrelationId = crypto.randomUUID();
    return activeLaunchCv.value;
}

/** Clears the active launch context. Call on player unmount. */
export function clearLaunchContext(): void {
    activeLaunchCv = null;
    launchCorrelationId = null;
}

/** True when a launch context is active. */
export function hasLaunchContext(): boolean {
    return activeLaunchCv !== null;
}

/**
 * Returns the base CV value of the active launch context without incrementing.
 * Useful for logging/diagnostics. Returns null when no context is active.
 */
export function getLaunchCvBase(): string | null {
    return activeLaunchCv?.value ?? null;
}

/**
 * Returns the next CV value for an outbound request. If a launch context is
 * active, increments it (producing BASE.N). Returns null when no launch
 * context is active — callers should fall back to a fresh root CV.
 */
export function nextRequestCv(): string | null {
    if (activeLaunchCv) {
        return activeLaunchCv.increment();
    }
    return null;
}

/**
 * Returns the launch-scoped correlationId (GUID). This value stays constant
 * for every request within the same launch context, unlike MS-CV which
 * increments per request.  Returns null when no launch context is active.
 */
export function getLaunchCorrelationId(): string | null {
    return launchCorrelationId;
}
